import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { chatApi, streamAssistantChat } from "@/lib/api";
import { Chip } from "@/components/common/StatusBadge";
import { cn } from "@/lib/utils";

const SESSION_KEY = "goldys.chat_session";
const SUGGESTIONS = ["How do I wash silk?", "Dry clean a wool coat?", "Remove red wine stains", "What are your prices?"];

export default function Assistant() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    setSessionId(sid);
    chatApi
      .getHistory(sid)
      .then((h) => setMessages(h.filter((m) => m.role === "user" || m.role === "assistant").map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || streaming || !sessionId) return;
    setInput("");
    setStreaming(true);
    setMessages((p) => [...p, { role: "user", content: trimmed }, { role: "assistant", content: "" }]);
    try {
      await streamAssistantChat(sessionId, trimmed, (delta) => {
        setMessages((p) => {
          const copy = [...p];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant") copy[copy.length - 1] = { ...last, content: last.content + delta };
          return copy;
        });
      });
    } catch (e) {
      toast.error(e.message || "Something went wrong");
      setMessages((p) => {
        const copy = [...p];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) copy.pop();
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-10rem)] max-w-2xl flex-col">
      <div className="flex items-center gap-3 py-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground"><Sparkles className="h-5 w-5" /></div>
        <div>
          <h1 className="font-display text-lg font-bold">Care Assistant</h1>
          <p className="text-xs text-muted-foreground">Stain rescue, fabric care & shop FAQs</p>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto py-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center gap-3 pt-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--brand-tertiary))] text-primary"><Sparkles className="h-6 w-6" /></div>
            <p className="font-display text-lg font-bold">Hi, I'm the Goldys assistant</p>
            <p className="text-sm text-muted-foreground">Ask me anything about garment care or our services.</p>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <Chip key={s} onClick={() => send(s)} testId={`assistant-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>{s}</Chip>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              <div className={cn("max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm", m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground")}>
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-border py-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about garment care…" data-testid="assistant-input" className="flex-1 rounded-full border border-border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary" />
        <button type="submit" disabled={!input.trim() || streaming} data-testid="assistant-send" className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
